<script setup lang="ts">
import {ExtendedUser, GpgKey, SshKey} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProfileLayout from "@/Pages/settings/layouts/ProfileLayout.vue";
import {useDate} from "@/composables/date";
import {Head, router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import DeleteSshKeyDialog from "@/Pages/settings/keys/components/DeleteSshKeyDialog.vue";
import DeleteGpgKeyDialog from "@/Pages/settings/keys/components/DeleteGpgKeyDialog.vue";

interface Props {
    user: ExtendedUser,
    sshKeys: Array<SshKey>
    gpgKeys: Array<GpgKey>
    emailAddresses: Array<string>
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const {shortDate} = useDate()
</script>

<template>
    <Head title="SSH & PGP keys" />
    <profile-layout
        :user="props.user"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            SSH keys
            <success-btn
                size="small"
                rounded="lg"
                class="float-end"
                @click="router.visit(route('settings.ssh_keys.create'))"
            >
                New SSH key
            </success-btn>
        </h1>
        <v-divider class="my-5" />
        <p
            v-if="sshKeys.length === 0"
            class="text-body-2"
        >
            There are no SSH keys associated with your account.
        </p>
        <div v-else>
            <p class="text-body-2">
                This is a list of SSH keys associated with your account. SSH keys will be automatically shared with any
                project you have access to. Remove any keys that you do not recognize.
            </p>
            <div class="font-weight-bold mt-3 mb-1">
                Authentication keys
            </div>
            <v-list
                rounded="lg"
                border="sm"
            >
                <v-list-item
                    v-for="(sshKey, index) in props.sshKeys"
                    :key="sshKey.hash"
                    :class="index < props.sshKeys.length - 1 ? 'border-b' : null"
                    :slim="true"
                    class="py-3"
                >
                    <template
                        #prepend
                    >
                        <v-row>
                            <v-col
                                cols="12"
                                class="pa-0"
                            >
                                <v-icon
                                    class="text-center ml-8"
                                    color="black"
                                    size="36"
                                >
                                    mdi-key-outline
                                </v-icon>
                            </v-col>
                            <v-col
                                cols="12"
                                class="pa-0 text-center"
                            >
                                <span class="border-sm rounded-lg pa-1 text-caption text-grey-darken-2">SSH</span>
                            </v-col>
                        </v-row>
                    </template>
                    <v-list-item-title
                        v-if="sshKey.name"
                        class="font-weight-bold text-body-2"
                    >
                        {{ sshKey.name }}
                    </v-list-item-title>
                    <v-list-item-title class="text-body-2">
                        <code
                            class="text-black"
                            style="font-size: 12px;"
                        >SHA256:{{ sshKey.hash }}</code>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        Added on {{ shortDate(sshKey.created_at) }}
                    </v-list-item-subtitle>
                    <template #append>
                        <delete-ssh-key-dialog :id="sshKey.id" />
                    </template>
                </v-list-item>
            </v-list>
        </div>

        <h1 class="font-weight-medium text-h5 mt-15">
            PGP keys
            <success-btn
                size="small"
                rounded="lg"
                class="float-end"
                @click="router.visit(route('settings.gpg_keys.create'))"
            >
                New PGP key
            </success-btn>
        </h1>
        <v-divider class="my-5" />
        <p
            v-if="gpgKeys.length === 0"
            class="text-body-2"
        >
            There are no PGP keys associated with your account.
        </p>
        <div v-else>
            <p class="text-body-2">
                This is a list of PGP keys associated with your account. PGP keys will be automatically shared with any
                project you have access to. Remove any keys that you do not recognize.
            </p>
            <div class="font-weight-bold mt-3 mb-1">
                Signing keys
            </div>
            <v-list
                rounded="lg"
                border="sm"
            >
                <v-list-item
                    v-for="(gpgKey, index) in props.gpgKeys"
                    :key="gpgKey.key_id"
                    :class="index < props.gpgKeys.length - 1 ? 'border-b' : null"
                    :slim="true"
                    class="pt-1 pb-2"
                >
                    <template
                        #prepend
                    >
                        <v-row>
                            <v-col
                                cols="12"
                                class="pa-0"
                            >
                                <v-icon
                                    class="text-center ml-8"
                                    color="black"
                                    size="36"
                                >
                                    mdi-key-outline
                                </v-icon>
                            </v-col>
                            <v-col
                                cols="12"
                                class="pa-0 text-center"
                            >
                                <span class="border-sm rounded-lg pa-1 text-caption text-grey-darken-2">PGP</span>
                            </v-col>
                        </v-row>
                    </template>
                    <v-list-item-title
                        v-if="gpgKey.name"
                        class="font-weight-bold text-body-2"
                    >
                        {{ gpgKey.name }}
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2 pb-1"
                    >
                        Email address:
                        <span
                            v-for="email in gpgKey.email_addresses"
                            :key="email"
                            class="border-sm rounded-lg text-caption text-grey-darken-2 d-flex"
                            style="width: fit-content"
                        >
                            <span class="mx-1">{{ email }}</span>
                            <v-tooltip
                                v-if="!emailAddresses.includes(email)"
                                location="bottom"
                                max-width="300"
                                content-class="bg-black rounded-lg text-white text-body-2 font-weight-medium"
                            >
                                <template #activator="{ props: attrs }">
                                    <a
                                        v-bind="attrs"
                                        class="bg-grey-lighten-4 border-s-sm px-1 underline-hover text-grey-darken-4"
                                        :href="route('settings.keys.index')"
                                    >
                                        Unverified
                                    </a>
                                </template>
                                Only PGP public keys belonging to the verified email addresses associated with your account will be allowed by projects.
                            </v-tooltip>
                        </span>
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2 pb-1"
                    >
                        Key ID: <code
                            class="font-weight-regular"
                        >{{ gpgKey.key_id }}</code>
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2 pb-1"
                    >
                        Subkeys: <code
                            class="font-weight-regular"
                        >{{ gpgKey.sub_keys.toString() }}</code>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        Added on {{ shortDate(gpgKey.created_at) }}
                    </v-list-item-subtitle>

                    <template #append>
                        <delete-gpg-key-dialog :id="gpgKey.id" />
                    </template>
                </v-list-item>
            </v-list>
        </div>
    </profile-layout>
</template>

<style scoped>
.underline-hover {
    text-decoration: none;
}

.underline-hover:hover {
    text-decoration: underline;
}
</style>
